import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { findClosest } from '@helpers/utils';

import { getHotelMetaSearchState, getTranslationsState } from '@redux/selectors';

import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';
import { Translations } from '@wegoTypes/translations';

interface ReviewScoreRange {
  id: number;
  minScore: number;
  maxScore: number;
  label: string;
  count: number;
}

const reviewScoresRange = [-1, 51, 61, 71, 81, 91, 101];
export const useReviewScoreManager = () => {
  const translations: Translations = useSelector(getTranslationsState);
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const [selectedRangeIds, setSelectedRangeIds] = useState<Array<number>>([]);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const locationState = state as {
    impressionType: 'sort' | 'filter' | 'page_load' | 'map_load';
    eventTrackingObj?: Record<string, any>;
  };

  const reviewScoreSearchParam = searchParams.get('guest_score');

  const selectedReviewScores = useMemo<Array<number>>(() => {
    if (!!reviewScoreSearchParam) {
      const parsedScores = reviewScoreSearchParam
        .split(',')
        .map((reviewScore) => parseInt(reviewScore));

      // If we have more than 2 values, it's multiple individual ranges - preserve them as-is
      if (parsedScores.length > 2) {
        return parsedScores;
      }

      // For old single range format, use the closest matching logic
      return parsedScores
        .map((reviewScore) => reviewScoresRange[findClosest(reviewScoresRange, reviewScore) ?? 0])
        .sort((a, b) => a - b);
    }
    return [reviewScoresRange[0], reviewScoresRange[reviewScoresRange.length - 1]];
  }, [reviewScoreSearchParam]);

  const onSelectedReviewScoresChanged = useCallback<(reviewScores: Array<number>) => void>(
    (reviewScores: Array<number>) => {
      searchParams.delete('guest_score');

      // Check if this is the full range (no filters applied)
      const isFullRange =
        reviewScores.length === 2 &&
        reviewScores[0] === reviewScoresRange[0] &&
        reviewScores[1] === reviewScoresRange[reviewScoresRange.length - 1];

      if (!isFullRange) {
        searchParams.set('guest_score', reviewScores.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              review_scores: reviewScores,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  // Calculate review score ranges with counts
  const reviewScoreRanges = useMemo<Array<ReviewScoreRange>>(() => {
    if (!hotelMetaSearch?.hotelViewModels) return [];

    const ranges: Array<ReviewScoreRange> = [
      {
        id: 5,
        minScore: 91,
        maxScore: 101,
        label: translations.review_score_excellent as string,
        count: 0,
      },
      {
        id: 4,
        minScore: 81,
        maxScore: 90,
        label: translations.review_score_verygood as string,
        count: 0,
      },
      {
        id: 3,
        minScore: 71,
        maxScore: 80,
        label: translations.review_score_good as string,
        count: 0,
      },
      {
        id: 2,
        minScore: 61,
        maxScore: 70,
        label: translations.review_score_pleasant as string,
        count: 0,
      },
      {
        id: 1,
        minScore: 51,
        maxScore: 60,
        label: translations.review_score_fair as string,
        count: 0,
      },
      {
        id: 0,
        minScore: -1,
        maxScore: 50,
        label: translations.review_score_poor as string,
        count: 0,
      },
    ];

    // Count hotels for each range
    hotelMetaSearch.hotelViewModels.forEach((hotelViewModel) => {
      const reviewScore = hotelViewModel.hotel.reviews?.[0]?.score;
      if (reviewScore !== undefined) {
        const range = ranges.find((r) => reviewScore >= r.minScore && reviewScore <= r.maxScore);
        if (range) {
          range.count++;
        }
      }
    });

    return ranges.filter((range) => range.count > 0);
  }, [hotelMetaSearch?.hotelViewModels, translations]);

  // Sync parent's selectedReviewScores with local selectedRangeIds
  useEffect(() => {
    if (selectedReviewScores.length >= 2 && reviewScoreRanges.length > 0) {
      // Check if this is the full range (no filters applied)
      const isFullRange =
        selectedReviewScores.length === 2 &&
        selectedReviewScores[0] === reviewScoresRange[0] &&
        selectedReviewScores[1] === reviewScoresRange[reviewScoresRange.length - 1];

      if (isFullRange) {
        setSelectedRangeIds([]);
      } else if (selectedReviewScores.length > 2) {
        // Multiple individual ranges format: [minScore1, maxScore1, minScore2, maxScore2, ...]
        const coveredRangeIds: Array<number> = [];
        for (let i = 0; i < selectedReviewScores.length; i += 2) {
          const minScore = selectedReviewScores[i];
          const maxScore = selectedReviewScores[i + 1];

          const range = reviewScoreRanges.find(
            (r) => r.minScore === minScore && r.maxScore === maxScore,
          );
          if (range) {
            coveredRangeIds.push(range.id);
          }
        }
        setSelectedRangeIds(coveredRangeIds);
      } else {
        // Legacy format: single continuous range [minScore, maxScore]
        const [minScore, maxScore] = selectedReviewScores;
        const coveredRangeIds = reviewScoreRanges
          .filter((range) => range.minScore >= minScore && range.maxScore <= maxScore)
          .map((range) => range.id);
        setSelectedRangeIds(coveredRangeIds);
      }
    }
  }, [selectedReviewScores, reviewScoreRanges]);

  const handleReviewScoreClick = (range: ReviewScoreRange) => {
    const isCurrentlySelected = selectedRangeIds.includes(range.id);
    let newSelectedRangeIds: Array<number>;

    if (isCurrentlySelected) {
      newSelectedRangeIds = selectedRangeIds.filter((id) => id !== range.id);
    } else {
      newSelectedRangeIds = [...selectedRangeIds, range.id];
    }

    setSelectedRangeIds(newSelectedRangeIds);

    // Convert selected range IDs to format for filtering
    if (newSelectedRangeIds.length === 0) {
      // No ranges selected - reset to full range
      onSelectedReviewScoresChanged([
        reviewScoresRange[0],
        reviewScoresRange[reviewScoresRange.length - 1],
      ]);
    } else {
      // Pass individual ranges as pairs: [minScore1, maxScore1, minScore2, maxScore2, ...]
      // Length > 2 indicates multiple individual ranges
      const selectedRanges = reviewScoreRanges.filter((r) => newSelectedRangeIds.includes(r.id));

      const rangeScores: Array<number> = [];

      selectedRanges.forEach((range) => {
        rangeScores.push(range.minScore, range.maxScore);
      });

      onSelectedReviewScoresChanged(rangeScores);
    }
  };

  const onReviewScoreToggle = (id: number) => {
    const range = reviewScoreRanges.find((r) => r.id === id);
    if (range) {
      handleReviewScoreClick(range);
    }
  };

  return {
    reviewScoreRanges,
    selectedRangeIds,
    onReviewScoreToggle,
  };
};
